// Penghubung hak akses: gabungkan setelan grup + override karyawan + bawaan peran,
// lalu jawab satu pertanyaan yang dipakai seluruh aplikasi: boleh lihat? boleh ubah?
//
// Disimpan di tabel `settings` (bukan kolom karyawan) supaya tak perlu migrasi dan
// tulisnya sudah dijaga RLS Owner/Manager.
// ponytail: satu baris berisi seluruh override karyawan. Pindahkan ke kolom `employees.perms`
// kalau karyawannya sudah ratusan dan barisnya jadi besar.
import { getSetting, saveSetting } from './settings';
import { currentUser } from './session';
import { MENU_META } from './shell';
import { levelOf, cariBerlapis, type Level, type PetaAkses } from '@domain/perms';
import { BAWAAN_AKSI, terkunciUntuk } from '@domain/perms.registry';

export type { Level, PetaAkses };

/** Setelan per grup (peran): {role: {key: level}} — sparse. */
export const aksesRole = (): Record<string, PetaAkses> => getSetting('akses', {});
/** Override per karyawan: {empId: {key: level}} — sparse, menang atas setelan grup. */
export const aksesUser = (): Record<string, PetaAkses> => getSetting('akses_user', {});

export const simpanAksesRole = (v: Record<string, PetaAkses>): Promise<void> => saveSetting('akses', v);
export const simpanAksesUser = (v: Record<string, PetaAkses>): Promise<void> => saveSetting('akses_user', v);

/**
 * Bawaan sebuah kunci untuk satu peran = perilaku aplikasi hari ini.
 * Menu yang perannya tak terdaftar → tak bisa diakses; sisanya bisa diubah,
 * kecuali aksi yang memang sudah dibatasi (lihat BAWAAN_AKSI).
 */
export function bawaanUntuk(role: string, key: string): Level | undefined {
  const menu = key.split('.')[0]!;
  const m = MENU_META().find((x) => x.id === menu);
  // Kunci yang menunya tak dikenal (salah ketik, id menu berubah) → TUTUP, jangan buka.
  // Kalau dibiarkan `undefined`, level jatuh ke bawaan akar 'full' dan semua orang lolos diam-diam.
  if (!m) return 'none';
  if (key === menu) return !m.roles || m.roles.includes(role as never) ? 'full' : 'none';
  return BAWAAN_AKSI[role]?.[key];
}

/** Level efektif untuk peran + karyawan tertentu. Dipakai juga oleh layar Hak Akses (pratinjau). */
/**
 * Kunci lama → kunci baru. Menu 📣 Penagihan dilebur jadi sub-tab ⚠️ Perlu Diperhatikan
 * (21 Jul); toko yang sudah mencabut menu itu dari SPV/CS akan kehilangan pencabutannya
 * kalau setelan lamanya tak dibaca — artinya mereka bisa menagih lagi tanpa ada yang mengubah
 * apa pun. Dipetakan saat dibaca supaya tak perlu menulis ulang setelan siapa pun.
 */
const ALIAS_LAMA: Record<string, string> = {
  'perlu_diperhatikan.tagih': 'penagihan',
  'perlu_diperhatikan.ingatkan': 'penagihan',
  'perlu_diperhatikan.nudge': 'penagihan',
  // Menu 👔 Karyawan & 🏢 Cabang dihapus dari sidebar (21 Jul), pindah jadi tab Pengaturan.
  // Tanpa alias ini, toko yang sudah mencabut akses Karyawan dari SPV akan membukanya lagi.
  'pengaturan.karyawan': 'karyawan',
  'pengaturan.karyawan.tambah': 'karyawan.tambah',
  'pengaturan.karyawan.ubah': 'karyawan.ubah',
  'pengaturan.karyawan.hapus': 'karyawan.hapus',
  'pengaturan.karyawan.suspend': 'karyawan.suspend',
  'pengaturan.cabang': 'cabang',
  'pengaturan.cabang.tambah': 'cabang.tambah',
  'pengaturan.cabang.ubah': 'cabang.ubah',
  'pengaturan.cabang.hapus': 'cabang.hapus',
  'pengaturan.cabang.gembok': 'cabang.gembok',
  // Menu Laba-Rugi & Arus Kas dilebur ke 💰 Keuangan (21 Jul) — pencabutan lama tetap berlaku.
  'keuangan': 'labarugi',
  'keuangan.export': 'labarugi.export',
};

export function levelFor(role: string, empId: number | string | null, key: string): Level {
  if (role === 'Owner') return 'full';                       // Owner tak pernah dibatasi
  // Kunci pemisahan tugas (CS tak memverifikasi, Finance tak melunasi, kelola akun Owner-only)
  // ditegakkan DI SINI, bukan hanya dengan men-disable dropdown di layar Hak Akses. Setelan
  // tersimpan di tabel yang bisa ditulis lewat API, jadi penjagaan yang hanya ada di layar bisa
  // dilewati tanpa membuka layarnya.
  if (terkunciUntuk(role, key)) return 'none';
  const cari = cariBerlapis(empId != null ? aksesUser()[String(empId)] : undefined, aksesRole()[role]);
  const cariAlias = (k: string): Level | undefined => {
    const v = cari(k);
    if (v !== undefined) return v;
    const lama = ALIAS_LAMA[k];
    return lama ? cari(lama) : undefined;
  };
  return levelOf(key, (k) => cariAlias(k) ?? bawaanUntuk(role, k));
}

const akuLevel = (key: string): Level => {
  const u = currentUser();
  if (!u) return 'none';
  return levelFor(u.role, u.id, key);
};

/** Boleh membuka / melihat. */
export const bisaBuka = (key: string): boolean => akuLevel(key) !== 'none';
/** Boleh mengubah (tambah/edit/hapus). "Lihat saja" → false. */
export const bisaUbah = (key: string): boolean => akuLevel(key) === 'full';
export const levelSaya = akuLevel;
